import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useTicketsStore } from 'pages/ticketV2/store/ticketsStore';
import {
  IS_MERCH_COUNT_IN_TOT,
  RedLinkButton,
  getProductDescription,
  getProductName,
} from 'pages/ticketV2/common';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';

const currencyFormatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
});

const MerchContainer = ({ fetchedMerch, locale }) => {
  const theme = useTheme();
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const intl = useIntl();
  const i18nCommon = translations.TicketsV2(intl).Common;
  const i18nMerch = translations.TicketsV2(intl).Merch;

  const { merchInCart, isCartFull } = useTicketsStore();
  const [merchQuantity, setMerchQuantity] = useState({});
  const [expandedCardsIdx, setExpandedCardsIdx] = useState([]);

  useEffect(() => {
    if (fetchedMerch?.length) {
      const quantities = {};
      fetchedMerch.map((t) => {
        const currentProdQty =
          merchInCart?.find((p) => p.id === t.id)?.quantity || 0;
        quantities[t.id] = currentProdQty;
      });
      setMerchQuantity(quantities);
    }
  }, [fetchedMerch]);

  useEffect(() => {
    if (Object.keys(merchQuantity)?.length > 0) {
      const _cart = fetchedMerch.map((pro) => ({
        ...pro,
        quantity: merchQuantity[pro.id],
        isMerchandising: true,
      }));
      useTicketsStore.setState({
        merchInCart: _cart,
      });
    }
  }, [merchQuantity]);

  const onAddClick = (id) => {
    if (merchQuantity[id] < 99)
      setMerchQuantity((prev) => ({
        ...prev,
        [id]: prev[id] + 1,
      }));
  };

  const onRemoveClick = (id) => {
    setMerchQuantity((prev) => ({ ...prev, [id]: prev[id] - 1 }));
  };

  const toggleDetails = (idx) => {
    const isExpanded = expandedCardsIdx.find((index) => index === idx);
    if (isExpanded) {
      setExpandedCardsIdx(expandedCardsIdx.filter((index) => index !== idx));
    } else {
      setExpandedCardsIdx((prev) => [...prev, idx]);
    }
  };

  return (
    <Box
      sx={{
        width: isLgDown ? '100%' : '64%',
        ...(!isLgDown && {
          pr: '70px',
        }),
      }}
    >
      <div>
        <Typography variant="xl">{i18nMerch.title}</Typography>
        <Typography
          variant="body1"
          sx={{
            mb: '5px',
            mt: isSmDown ? '10px' : '25px',
          }}
        >
          {i18nMerch.titleSubtext}
        </Typography>
      </div>
      <Stack sx={{ mt: '25px' }} direction={'column'} gap={'25px'}>
        {fetchedMerch.map((product, idx) => (
          <Stack
            key={product.id}
            sx={{
              p: '25px 20px',
              bgcolor: 'white',
              border: `2px solid ${
                merchQuantity[product.id] === 0
                  ? theme.palette.grey[200]
                  : theme.palette.primary.main
              }`,
            }}
          >
            {isLgDown ? (
              <Stack
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'flex-start'}
                sx={{
                  mb: expandedCardsIdx.find((index) => index === idx + 1)
                    ? '15px'
                    : '30px',
                }}
              >
                <Typography
                  variant="m-bold"
                  sx={{ mb: '5px' }}
                  fontFamily={'Suisse-Intl'}
                >
                  {getProductName(product, locale)}
                </Typography>
                <RedLinkButton
                  onClick={() => toggleDetails(idx + 1)}
                  msg={i18nCommon.details}
                  icon={
                    expandedCardsIdx.find((index) => index === idx + 1) ? (
                      <ExpandLessIcon color="primary" />
                    ) : (
                      <ExpandMoreIcon color="primary" />
                    )
                  }
                />
              </Stack>
            ) : (
              <Typography
                variant="m-bold"
                sx={{ mb: '5px' }}
                fontFamily={'Suisse-Intl'}
              >
                {getProductName(product, locale)}
              </Typography>
            )}
            {!isLgDown ||
            expandedCardsIdx.find((index) => index === idx + 1) ? (
              <Typography
                variant="body2"
                sx={{ mb: '15px' }}
                fontFamily={'Suisse-Intl'}
              >
                {getProductDescription(product, locale)}
              </Typography>
            ) : null}
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
            >
              <Typography
                variant="body2-bold"
                color={'grey.800'}
                fontFamily={'Suisse-Intl'}
              >
                {product?.current_price
                  ? currencyFormatter.format(product?.current_price)
                  : null}
              </Typography>
              <Stack direction={'row'}>
                <Button
                  disabled={merchQuantity[product.id] === 0}
                  variant="contained"
                  sx={{
                    width: '45px',
                    height: '45px',
                    padding: 0,
                    minWidth: 0,
                  }}
                  onClick={() => onRemoveClick(product.id)}
                >
                  <RemoveIcon fontSize="20px" />
                </Button>
                <Stack
                  alignItems={'center'}
                  justifyContent={'center'}
                  sx={{ width: '40px' }}
                >
                  <Typography variant="body2-bold" fontFamily={'Suisse-Intl'}>
                    {merchQuantity[product.id]}
                  </Typography>
                </Stack>
                <Button
                  disabled={
                    (isCartFull && IS_MERCH_COUNT_IN_TOT) ||
                    merchQuantity[product.id] === 99
                  }
                  variant="contained"
                  sx={{
                    width: '45px',
                    height: '45px',
                    padding: 0,
                    minWidth: 0,
                  }}
                  onClick={() => onAddClick(product.id)}
                >
                  <AddIcon fontSize="20px" />
                </Button>
              </Stack>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};
export default MerchContainer;
